import React, { Component } from "react";
import { Button, Input, notification, Modal, Spin } from "antd";
import {
  SearchOutlined,
  HomeOutlined,
  CheckCircleOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { noAuthGetInfo } from "../api/Apis";

// ── Brand tokens ───────────────────────────────────────────────────────────────
const NAVY  = "#1A2280";
const GOLD  = "#D4921A";
const WHITE = "#FFFFFF";

// Profile type labels
const PROFILE_LABELS = {
  AG: "Agent",
  L:  "Lender",
  B:  "Buyer",
  S:  "Seller",
  V:  "Vendor",
};

const PROFILE_COLORS = {
  AG: "#1A2280",
  L:  "#276749",
  B:  "#D4921A",
  S:  "#805AD5",
  V:  "#E53E3E",
};

// ── Main component ─────────────────────────────────────────────────────────────
class ProspectMatchPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Step 1 — address search
      searchAddress:     "",
      publicRecord:      null,   // { address, owner, yearBuilt, sqft, ... }
      searching:         false,
      searchError:       "",

      // Step 2 — confirm ownership
      showConfirmModal:  false,
      certifyChecked:    false,
      confirming:        false,
      confirmed:         false,

      // Step 3 — prospect matches
      matches:           [],
      loadingMatches:    false,
      matchError:        "",
    };
  }

  // ── Step 1: Look up address via public records ────────────────────────────
  handleSearch = async () => {
    const { searchAddress } = this.state;
    if (!searchAddress.trim()) {
      notification.error({ message: "Please enter your property address" });
      return;
    }
    this.setState({ searching: true, searchError: "", publicRecord: null });
    try {
      // Use the existing mhb-map endpoint for geocode + public records
      const resp = await fetch(
        `https://ai.myhomebuddha.com/.netlify/functions/mhb-map?address=${encodeURIComponent(searchAddress)}&records=1`
      );
      const data = await resp.json();
      if (!data.ok) throw new Error(data.error || "Address not found");

      this.setState({
        publicRecord: {
          address:       data.address || searchAddress,
          lat:           data.lat,
          lng:           data.lng,
          owner:         data.records?.owner         || null,
          yearBuilt:     data.records?.yearBuilt     || null,
          sqft:          data.records?.squareFootage || null,
          propertyType:  data.records?.propertyType  || null,
          lastSaleDate:  data.records?.lastSaleDate   || null,
          lastSalePrice: data.records?.lastSalePrice  || null,
        },
        searching: false,
      });
    } catch(e) {
      this.setState({ searching: false, searchError: e.message });
    }
  };

  // ── Step 2: Ownership confirmation modal ─────────────────────────────────
  openConfirm = () => this.setState({ showConfirmModal: true, certifyChecked: false });
  closeConfirm = () => this.setState({ showConfirmModal: false });

  handleConfirm = async () => {
    const { certifyChecked } = this.state;
    if (!certifyChecked) {
      notification.error({ message: "Please certify that you are the owner of this home" });
      return;
    }
    this.setState({ confirming: true });

    // Short delay for UX then proceed to lookup
    await new Promise(r => setTimeout(r, 800));
    this.setState({ confirming: false, confirmed: true, showConfirmModal: false });
    this.loadMatches();
  };

  // ── Step 3: Query backend for prospect matches ───────────────────────────
  loadMatches = async () => {
    const { publicRecord } = this.state;
    if (!publicRecord) return;
    this.setState({ loadingMatches: true, matchError: "" });

    try {
      // Call the portal API — proxies to mhb-data Lambda scan_by_address
      const userDetails = JSON.parse(localStorage.getItem("userDetails") || "{}");
      const token = localStorage.getItem("token") || "";

      const resp = await fetch(
        `${process.env.REACT_APP_API_GATEWAY_URL || "https://api.myhomebuddha.com"}/mhb/data`,
        {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action:  "scan_by_address",
            token,
            address: publicRecord.address,
          }),
        }
      );
      const data = await resp.json();
      if (!data.ok) throw new Error(data.error || "Lookup failed");
      this.setState({ matches: data.matches || [], loadingMatches: false });
    } catch(e) {
      this.setState({ matchError: e.message, loadingMatches: false });
    }
  };

  // ── Renders ───────────────────────────────────────────────────────────────

  renderSearchStep() {
    const { searchAddress, searching, searchError, publicRecord } = this.state;
    return (
      <div>
        {/* Hero banner */}
        <div style={{
          background: `linear-gradient(135deg, ${NAVY} 0%, #2A3390 100%)`,
          borderRadius: "14px", padding: "24px 24px 20px", marginBottom: "20px",
          boxShadow: "0 4px 20px rgba(26,34,128,0.15)",
        }}>
          <div style={{ fontSize: "32px", marginBottom: "8px" }}>🏡</div>
          <div style={{ color: WHITE, fontWeight: 800, fontSize: "20px", marginBottom: "6px" }}>
            Before You List — See Who's Already Interested
          </div>
          <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "13px", lineHeight: 1.7 }}>
            Agents, buyers, and lenders save properties they're watching on our prospecting map.
            Enter your address to see if anyone has already marked your home — and reach out
            before you even list.
          </div>
          <div style={{
            marginTop: "14px", background: "rgba(212,146,26,0.15)", border: "1px solid rgba(212,146,26,0.4)",
            borderRadius: "8px", padding: "10px 14px", fontSize: "12px",
            color: "#FFD98E", display: "flex", alignItems: "center", gap: "8px",
          }}>
            🔑 Claim your home to unlock your prospect list — 100% free
          </div>
        </div>

        {/* Address search */}
        <div style={{
          background: WHITE, borderRadius: "12px", padding: "20px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.07)", marginBottom: "16px",
        }}>
          <div style={{ fontWeight: 700, color: NAVY, fontSize: "14px", marginBottom: "10px" }}>
            Enter Your Property Address
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <Input
              size="large"
              prefix={<HomeOutlined style={{ color: NAVY }} />}
              placeholder="e.g. 22 Leonard St, Gardner, MA 01440"
              value={searchAddress}
              onChange={e => this.setState({ searchAddress: e.target.value })}
              onPressEnter={this.handleSearch}
              style={{ borderRadius: "8px", fontSize: "13px" }}
            />
            <Button
              type="primary"
              size="large"
              icon={<SearchOutlined />}
              loading={searching}
              onClick={this.handleSearch}
              style={{ borderRadius: "8px", background: NAVY, borderColor: NAVY, minWidth: "80px" }}
            >
              Search
            </Button>
          </div>
          {searchError && (
            <div style={{ color: "#E53E3E", fontSize: "12px", marginTop: "8px" }}>
              ⚠️ {searchError}
            </div>
          )}
        </div>

        {/* Public record result */}
        {publicRecord && (
          <div style={{
            background: WHITE, borderRadius: "12px", overflow: "hidden",
            boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
          }}>
            <div style={{
              background: `${NAVY}08`, padding: "12px 16px",
              borderBottom: `2px solid ${GOLD}`,
            }}>
              <div style={{ fontWeight: 800, color: NAVY, fontSize: "13px" }}>
                📍 Public Record Found
              </div>
            </div>
            <div style={{ padding: "16px" }}>
              <div style={{ fontWeight: 700, fontSize: "15px", color: "#1A202C", marginBottom: "12px" }}>
                {publicRecord.address}
              </div>
              <div style={{
                display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "16px",
              }}>
                {[
                  publicRecord.propertyType && ["Property Type", publicRecord.propertyType],
                  publicRecord.yearBuilt    && ["Year Built",    publicRecord.yearBuilt],
                  publicRecord.sqft         && ["Sq Footage",    publicRecord.sqft.toLocaleString() + " sq ft"],
                  publicRecord.owner        && ["Owner on Record", publicRecord.owner],
                  publicRecord.lastSaleDate && ["Last Sale",     publicRecord.lastSaleDate],
                  publicRecord.lastSalePrice && ["Last Sale Price", "$" + Number(publicRecord.lastSalePrice).toLocaleString()],
                ].filter(Boolean).map(([label, value]) => (
                  <div key={label} style={{
                    background: "#F7F9FC", borderRadius: "8px", padding: "8px 12px",
                  }}>
                    <div style={{ fontSize: "10px", fontWeight: 700, color: "#718096",
                      textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "2px" }}>
                      {label}
                    </div>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "#1A202C" }}>
                      {value}
                    </div>
                  </div>
                ))}
              </div>
              <Button
                type="primary"
                size="large"
                block
                icon={<CheckCircleOutlined />}
                onClick={this.openConfirm}
                style={{
                  background: GOLD, borderColor: GOLD, color: "#2A1600",
                  borderRadius: "50px", fontWeight: 800, height: "44px",
                }}
              >
                🔑 This is My Home — Claim It
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  renderMatches() {
    const { matches, loadingMatches, matchError, publicRecord } = this.state;

    if (loadingMatches) {
      return (
        <div style={{ textAlign: "center", padding: "60px 20px" }}>
          <Spin size="large" />
          <div style={{ marginTop: "16px", color: "#718096", fontSize: "13px" }}>
            Searching for interested parties...
          </div>
        </div>
      );
    }

    if (matchError) {
      return (
        <div style={{
          background: "#FFF5F5", border: "1px solid #FED7D7", borderRadius: "10px",
          padding: "16px", color: "#C53030", fontSize: "13px",
        }}>
          ⚠️ {matchError}
        </div>
      );
    }

    return (
      <div>
        {/* Confirmed header */}
        <div style={{
          background: `linear-gradient(135deg, #276749 0%, #2F855A 100%)`,
          borderRadius: "14px", padding: "18px 20px", marginBottom: "20px",
          boxShadow: "0 4px 16px rgba(39,103,73,0.2)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ fontSize: "28px" }}>✅</div>
            <div>
              <div style={{ color: WHITE, fontWeight: 800, fontSize: "15px" }}>
                Home Claimed Successfully
              </div>
              <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "12px", marginTop: "2px" }}>
                {publicRecord?.address}
              </div>
            </div>
          </div>
        </div>

        {matches.length === 0 ? (
          <div style={{
            background: WHITE, borderRadius: "12px", padding: "40px 24px",
            textAlign: "center", boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
          }}>
            <div style={{ fontSize: "40px", marginBottom: "12px" }}>🔍</div>
            <div style={{ fontWeight: 700, color: NAVY, fontSize: "16px", marginBottom: "8px" }}>
              No Matches Found Yet
            </div>
            <div style={{ color: "#718096", fontSize: "13px", lineHeight: 1.7, maxWidth: "380px", margin: "0 auto" }}>
              No agents or buyers have saved your address yet — but that doesn't mean there's no interest.
              List your home to reach the full network.
            </div>
            <Button
              type="primary"
              style={{
                marginTop: "20px", borderRadius: "50px",
                background: NAVY, borderColor: NAVY, fontWeight: 700,
              }}
              onClick={() => this.props.onProceedToList?.()}
            >
              ➕ List My Home Now
            </Button>
          </div>
        ) : (
          <div>
            {/* Match count banner */}
            <div style={{
              background: `${NAVY}08`, border: `1px solid ${NAVY}20`,
              borderRadius: "10px", padding: "12px 16px", marginBottom: "16px",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div style={{ fontWeight: 800, color: NAVY, fontSize: "14px" }}>
                🎯 {matches.length} Interested {matches.length === 1 ? "Party" : "Parties"} Found
              </div>
              <Button
                size="small"
                type="primary"
                onClick={() => this.props.onProceedToList?.()}
                style={{ borderRadius: "50px", background: GOLD, borderColor: GOLD,
                  color: "#2A1600", fontWeight: 700, fontSize: "11px" }}
              >
                ➕ List Your Home
              </Button>
            </div>

            {/* Match cards */}
            {matches.map((match, idx) => (
              <div key={match.prospectId || idx} style={{
                background: WHITE, borderRadius: "12px", marginBottom: "12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.07)", overflow: "hidden",
              }}>
                {/* Color accent bar by profile type */}
                <div style={{
                  height: "3px",
                  background: PROFILE_COLORS[match.userProfile] || NAVY,
                }} />
                <div style={{ padding: "14px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "10px" }}>
                    {/* Avatar */}
                    {match.photo ? (
                      <img src={match.photo} alt={match.name} style={{
                        width: "44px", height: "44px", borderRadius: "50%",
                        objectFit: "cover", flexShrink: 0,
                        border: `2px solid ${PROFILE_COLORS[match.userProfile] || NAVY}30`,
                      }} />
                    ) : (
                      <div style={{
                        width: "44px", height: "44px", borderRadius: "50%", flexShrink: 0,
                        background: `${PROFILE_COLORS[match.userProfile] || NAVY}15`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "18px",
                      }}>
                        <UserOutlined style={{ color: PROFILE_COLORS[match.userProfile] || NAVY }} />
                      </div>
                    )}

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 700, fontSize: "14px", color: "#1A202C" }}>
                          {match.name}
                        </span>
                        {match.userProfile && (
                          <span style={{
                            background: `${PROFILE_COLORS[match.userProfile] || NAVY}15`,
                            color: PROFILE_COLORS[match.userProfile] || NAVY,
                            border: `1px solid ${PROFILE_COLORS[match.userProfile] || NAVY}30`,
                            borderRadius: "50px", fontSize: "10px", fontWeight: 700,
                            padding: "1px 8px",
                          }}>
                            {PROFILE_LABELS[match.userProfile] || match.userProfile}
                          </span>
                        )}
                        {match.type === "favorite" && (
                          <span style={{
                            background: "#FFF8E6", color: GOLD,
                            border: `1px solid ${GOLD}40`,
                            borderRadius: "50px", fontSize: "10px", fontWeight: 700,
                            padding: "1px 8px",
                          }}>
                            ⭐ Favorited
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: "11px", color: "#718096", marginTop: "2px" }}>
                        Saved {match.savedAt || "recently"}
                      </div>
                    </div>
                  </div>

                  {/* Note */}
                  {match.note && (
                    <div style={{
                      background: "#F7F9FC", borderRadius: "8px", padding: "8px 12px",
                      fontSize: "12px", color: "#4A5568", marginBottom: "10px",
                      borderLeft: `3px solid ${GOLD}`,
                      fontStyle: "italic", lineHeight: 1.6,
                    }}>
                      "{match.note}"
                    </div>
                  )}

                  {/* Contact buttons */}
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    {match.phone && (
                      <a href={`tel:${match.phone}`} style={{
                        display: "flex", alignItems: "center", gap: "5px",
                        background: `${NAVY}08`, border: `1px solid ${NAVY}20`,
                        borderRadius: "50px", padding: "5px 12px",
                        color: NAVY, fontSize: "11px", fontWeight: 700,
                        textDecoration: "none",
                      }}>
                        <PhoneOutlined /> {match.phone}
                      </a>
                    )}
                    {match.email && (
                      <a href={`mailto:${match.email}?subject=Regarding your interest in ${publicRecord?.address}`}
                        style={{
                          display: "flex", alignItems: "center", gap: "5px",
                          background: `${GOLD}15`, border: `1px solid ${GOLD}40`,
                          borderRadius: "50px", padding: "5px 12px",
                          color: "#7A5010", fontSize: "11px", fontWeight: 700,
                          textDecoration: "none",
                        }}>
                        <MailOutlined /> {match.email}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* CTA to list */}
            <div style={{
              background: `linear-gradient(135deg, ${NAVY} 0%, #2A3390 100%)`,
              borderRadius: "12px", padding: "18px 20px", marginTop: "8px",
              textAlign: "center",
            }}>
              <div style={{ color: WHITE, fontWeight: 800, fontSize: "14px", marginBottom: "6px" }}>
                Ready to reach the full network?
              </div>
              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "12px", marginBottom: "14px" }}>
                List your home to be discovered by thousands more buyers and agents.
              </div>
              <Button
                type="primary"
                size="large"
                onClick={() => this.props.onProceedToList?.()}
                style={{
                  background: GOLD, borderColor: GOLD, color: "#2A1600",
                  borderRadius: "50px", fontWeight: 800,
                }}
              >
                ➕ List My Home Now
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  renderConfirmModal() {
    const { showConfirmModal, publicRecord, certifyChecked, confirming } = this.state;
    if (!publicRecord) return null;
    return (
      <Modal
        visible={showConfirmModal}
        title={null}
        footer={null}
        onCancel={this.closeConfirm}
        centered
        bodyStyle={{ padding: 0 }}
        className="modal-black-close"
      >
        <div style={{ overflow: "hidden", borderRadius: "8px" }}>
          {/* Header */}
          <div style={{
            background: `linear-gradient(135deg, ${NAVY}, #2A3390)`,
            padding: "20px 24px",
          }}>
            <div style={{ fontSize: "28px", marginBottom: "6px" }}>🔑</div>
            <div style={{ color: WHITE, fontWeight: 800, fontSize: "17px" }}>
              Claim Your Home
            </div>
            <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "12px", marginTop: "3px" }}>
              {publicRecord.address}
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: "20px 24px" }}>
            <div style={{
              background: "#F7F9FC", borderRadius: "10px", padding: "14px 16px",
              marginBottom: "16px", fontSize: "13px", color: "#4A5568", lineHeight: 1.7,
            }}>
              By claiming this home you'll be able to see a list of agents, buyers, and real estate
              professionals who have saved this address on the My Home Buddha prospecting map — along
              with any notes they added. Their contact information will be displayed so you can
              reach out directly.
            </div>

            {/* Certification checkbox */}
            <div
              onClick={() => this.setState({ certifyChecked: !certifyChecked })}
              style={{
                display: "flex", alignItems: "flex-start", gap: "12px",
                cursor: "pointer", padding: "12px 14px",
                background: certifyChecked ? "#F0FFF4" : "#FAFAFA",
                border: `2px solid ${certifyChecked ? "#276749" : "#E2E8F0"}`,
                borderRadius: "10px", marginBottom: "20px",
                transition: "all .15s",
              }}
            >
              <div style={{
                width: "20px", height: "20px", borderRadius: "4px", flexShrink: 0, marginTop: "1px",
                background: certifyChecked ? "#276749" : "white",
                border: `2px solid ${certifyChecked ? "#276749" : "#CBD5E0"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {certifyChecked && (
                  <span style={{ color: "white", fontSize: "12px", fontWeight: 700 }}>✓</span>
                )}
              </div>
              <div style={{ fontSize: "12px", color: "#2D3748", lineHeight: 1.6 }}>
                <strong>I certify that I am the owner</strong> of this property or am an authorized
                representative of the owner of <strong>{publicRecord.address}</strong>, and that I
                have the right to access this information.
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px" }}>
              <Button
                size="large"
                style={{ flex: 1, borderRadius: "50px" }}
                onClick={this.closeConfirm}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                size="large"
                loading={confirming}
                disabled={!certifyChecked}
                style={{
                  flex: 2, borderRadius: "50px",
                  background: certifyChecked ? "#276749" : undefined,
                  borderColor: certifyChecked ? "#276749" : undefined,
                  fontWeight: 800,
                }}
                onClick={this.handleConfirm}
              >
                ✅ Confirm & See My Prospects
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  render() {
    const { confirmed } = this.state;
    return (
      <div style={{ padding: "16px" }}>
        {confirmed ? this.renderMatches() : this.renderSearchStep()}
        {this.renderConfirmModal()}
      </div>
    );
  }
}

export default ProspectMatchPanel;